import { Fragment, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Accordion,
  AccordionButton,
  AccordionItem,
  AccordionPanel,
  Box,
  Flex,
  IconButton,
  Table,
  Tbody,
  Text,
} from '@chakra-ui/react';

import { FeatherIcon } from '../icon';
import { Spinner } from '../Spinner';

export type BaseDocumentUploadEntity = {
  fileId: string;
  uploadSuccess: boolean;
  isValid: boolean;
  uploadAborted?: boolean;
};

type Props<T> = {
  documentUploadEntities: (BaseDocumentUploadEntity & T)[];
  onClose: VoidFunction;
  onAbort: VoidFunction;
  renderDocumentUploadRow: (documentUploadEntity: BaseDocumentUploadEntity & T) => React.ReactNode;
};

export const UploadZoneView = <T,>({
  documentUploadEntities,
  onClose,
  onAbort,
  renderDocumentUploadRow,
}: Props<T>) => {
  const { t } = useTranslation('shared');

  const noOfUploads = documentUploadEntities.length;
  const noOfUploadsComplete = documentUploadEntities.filter((e) => e.uploadSuccess === true).length;
  const noOfErrors = documentUploadEntities.filter(
    (e) => e.isValid === false || e.uploadAborted === true,
  ).length;
  const canCloseWithoutWarning = noOfUploads === noOfUploadsComplete + noOfErrors;

  const icon = useMemo(() => {
    if (noOfErrors > 0) {
      return <FeatherIcon name='alert-octagon' color='secondary400' />;
    }
    if (noOfUploads > 0 && noOfUploadsComplete === noOfUploads) {
      return <FeatherIcon name='check' color='primary400' />;
    }
    return <Spinner />;
  }, [noOfErrors, noOfUploads, noOfUploadsComplete]);

  const titleColor = useMemo(() => {
    if (noOfErrors > 0) {
      return 'secondary600';
    }
    if (noOfUploads > 0 && noOfUploadsComplete === noOfUploads) {
      return 'primary600';
    }
    return 'neutral600';
  }, [noOfErrors, noOfUploads, noOfUploadsComplete]);

  const title = useMemo(() => {
    if (noOfUploads === noOfUploadsComplete) {
      return t('uploadZone.uploadComplete', { count: noOfUploadsComplete });
    }
    if (noOfErrors > 0 && noOfErrors === noOfUploads) {
      return t('uploadZone.uploadFailed');
    }
    if (noOfErrors > 0 && noOfErrors + noOfUploadsComplete === noOfUploads) {
      return t('uploadZone.partiallyUploaded');
    }
    return t('uploadZone.uploading', {
      count: noOfUploads - noOfErrors - noOfUploadsComplete,
    });
  }, [noOfErrors, noOfUploads, noOfUploadsComplete, t]);

  if (noOfUploads === 0) {
    return null;
  }

  return (
    <Box bg='white' borderRadius='lg' boxShadow='card'>
      <Accordion allowToggle defaultIndex={0}>
        <AccordionItem border={0}>
          {({ isExpanded }) => (
            <>
              <Flex
                bg={titleColor}
                borderTopRadius='lg'
                borderBottomRadius={isExpanded ? 'none' : 'lg'}
                py={1}>
                <AccordionButton
                  _hover={{ background: 'initial' }}
                  _focus={{ background: 'initial' }}
                  pr={0}>
                  {icon}
                  <Text as='span' flex='1' textAlign='left' variant='label' color='white' ml={3}>
                    {title}
                  </Text>
                </AccordionButton>
                <IconButton
                  icon={<FeatherIcon name='x' color='white' />}
                  aria-label={t('action.close')}
                  onClick={canCloseWithoutWarning ? onClose : onAbort}
                />
              </Flex>
              <AccordionPanel maxH='md' overflow='auto' py={2}>
                <Table variant='simple'>
                  <Tbody>
                    {documentUploadEntities.length > 0 &&
                      documentUploadEntities.map((documentUploadEntity) => (
                        <Fragment key={documentUploadEntity.fileId}>
                          {renderDocumentUploadRow(documentUploadEntity)}
                        </Fragment>
                      ))}
                  </Tbody>
                </Table>
              </AccordionPanel>
            </>
          )}
        </AccordionItem>
      </Accordion>
    </Box>
  );
};
